import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw, Save, ShieldCheck } from 'lucide-react';
import { Button } from 'components/ui/Button';
import { Checkbox } from 'components/forms';
import { useAbp } from 'core/hooks/useAbp';
import { AbpPolicies } from 'core/constants/AbpPolicies';
import { attributionsPlateformesAPI } from 'services/swagger-api';
import { GetBriquesDelegablesResponse } from 'api/attributions-plateformes';
import { InlineError, runOperation } from 'back-office/shared/components/rolesUi';

interface PlateformeBriquesDelegablesCardProps {
  plateformeId: string;
}

/**
 * Plafond délégable de la plateforme (admin) : les briques que la gouvernance du
 * Portail Plateforme aura le droit d'accorder à ses courtiers. Sans plafond
 * configuré, le profil de la nature de chaque courtier fait plafond par défaut.
 */
export const PlateformeBriquesDelegablesCard: React.FC<PlateformeBriquesDelegablesCardProps> = ({
  plateformeId
}) => {
  const { hasPermission } = useAbp();
  const peutGerer = hasPermission(AbpPolicies.ATELIER_ROLES_MANAGE);

  const [etat, setEtat] = useState<GetBriquesDelegablesResponse | null>(null);
  const [briques, setBriques] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await attributionsPlateformesAPI.getBriquesDelegables({ plateformeId: plateformeId });
      setEtat(res);
      setBriques(res.briques);
    } catch (err) {
      console.error('Erreur de chargement du plafond délégable:', err);
      setError('Impossible de charger le plafond délégable.');
    }
  }, [plateformeId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = async () => {
    setIsSaving(true);
    const err = await runOperation(() =>
      attributionsPlateformesAPI.setBriquesDelegables({
        plateformeId: plateformeId,
        setBriquesDelegablesCommand: { briques }
      })
    );
    setIsSaving(false);
    setError(err);
    if (!err) await reload();
  };

  const reset = async () => {
    setIsSaving(true);
    const err = await runOperation(() =>
      attributionsPlateformesAPI.resetBriquesDelegables({ plateformeId: plateformeId })
    );
    setIsSaving(false);
    setError(err);
    if (!err) await reload();
  };

  if (!peutGerer || !etat) {
    return null;
  }

  return (
    <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
      <header className="flex items-center justify-between gap-2 bg-primary px-4 py-2.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
          <ShieldCheck className="h-4 w-4 text-white/90" />
          Briques délégables
        </h3>
        {etat.estDefaut && (
          <span
            className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-medium text-white"
            title="Aucun plafond configuré : le profil de la nature de chaque courtier fait plafond."
          >
            Défaut
          </span>
        )}
      </header>
      <div className="space-y-2.5 px-4 py-3">
        <p className="text-xs text-gray-500">
          Le plafond de ce que la gouvernance de la plateforme peut accorder à ses courtiers
          depuis le Portail Plateforme. Sans plafond configuré, le profil de la nature de chaque
          courtier fait foi.
        </p>
        <div className="space-y-1">
          {etat.briquesDisponibles.map((brique) => (
            <Checkbox
              key={brique}
              name={`delegable-${brique}`}
              label={brique}
              checked={briques.includes(brique)}
              onChange={(checked) =>
                setBriques((prev) => (checked ? [...prev, brique] : prev.filter((b) => b !== brique)))
              }
            />
          ))}
        </div>
        <InlineError message={error} />
        <div className="flex items-center justify-end gap-2">
          {!etat.estDefaut && (
            <Button outline disabled={isSaving} onClick={reset} tooltip="Revenir au défaut (profil de la nature)">
              <RotateCcw data-slot="icon" />
            </Button>
          )}
          <Button color="primary" disabled={isSaving} onClick={save}>
            <Save data-slot="icon" /> Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};
